import type { ExtensionListItem } from '@kirin/api-client/contracts/extensions';
import type { ReactElement } from 'react';
import { Users } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { VerifiedPublisherIcon } from '@/components/shared/extensions';
import { cn } from '@/lib/utils';

interface PublisherDisplayProps {
  name: string;
  isVerified: boolean;
  className?: string;
}

export function PublisherDisplay({
  name,
  isVerified,
  className,
}: PublisherDisplayProps): ReactElement {
  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <VerifiedPublisherIcon isVerified={isVerified} />
      <span className="min-w-0 text-sm truncate" title={name}>
        {name}
      </span>
    </div>
  );
}

interface UsersCellProps {
  row: ExtensionListItem;
  onClick?: () => void;
}

/**
 * Clickable users cell that opens the user list modal.
 * Matches legacy ExtensionsTab implementation exactly.
 */
export function UsersCell({ row, onClick }: UsersCellProps) {
  const { userCount } = row;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onClick?.();
          }}
          className="inline-flex items-center gap-1.5 font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors cursor-pointer"
        >
          <Users className="h-3.5 w-3.5" />
          {userCount}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Users className="h-3 w-3" />
            <span>
              {userCount} user{userCount !== 1 ? 's' : ''} with this extension
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Click to see user list
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

interface VersionUsersCellProps {
  userCount: number;
  onClick?: () => void;
}

/**
 * Clickable users cell for the versions table in expanded rows.
 * Matches legacy ExtensionsTab implementation exactly.
 */
export function VersionUsersCell({
  userCount,
  onClick,
}: VersionUsersCellProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onClick?.();
          }}
          className="inline-flex items-center gap-1.5 font-medium text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors cursor-pointer"
        >
          <Users className="h-3.5 w-3.5" />
          {userCount}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Users className="h-3 w-3" />
            <span>
              {userCount} user{userCount !== 1 ? 's' : ''} with this version
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Click to see user list
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
